// src/components/PrintButton.js
//
// A single context-aware print button placed in AppHeader.
// For the Journal view, it opens a small modal asking "Today" or "Date Range".
// For all other views it prints immediately.

import React, { useState } from 'react';
import { Printer, X } from 'lucide-react';
import {
  printTasks,
  printJournalToday,
  printJournalRange,
  printGoals,
  printRoles,
  printCalendar,
} from '../printUtils';

export default function PrintButton({
  user,
  currentView,
  // props passed from each view so we print the right date/context
  currentDate,     // Date object — used by tasks + journal today
  selectedYear,    // number — used by goals
  selectedMonth,   // number — used by goals
}) {
  const [showModal, setShowModal]   = useState(false);
  const [printMode, setPrintMode]   = useState('today'); // 'today' | 'range'
  const [startDate, setStartDate]   = useState('');
  const [endDate, setEndDate]       = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  const handlePrint = async () => {
    setLoading(true);
    setError('');
    try {
      switch (currentView) {
        case 'tasks':
          await printTasks(user, currentDate || new Date());
          break;
        case 'notes': // Journal
          setShowModal(true);
          setLoading(false);
          return; // modal handles the rest
        case 'goals':
          await printGoals(
            user,
            selectedYear  || new Date().getFullYear(),
            selectedMonth || new Date().getMonth() + 1
          );
          break;
        case 'roles':
          await printRoles(user);
          break;
        case 'calendar':
          printCalendar();
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('[OPP] Print error:', err);
      setError('Print failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJournalPrint = async () => {
    if (printMode === 'range') {
      if (!startDate || !endDate) {
        setError('Please select both a start and end date.');
        return;
      }
      if (startDate > endDate) {
        setError('Start date must be before end date.');
        return;
      }
    }
    setLoading(true);
    setError('');
    try {
      if (printMode === 'today') {
        await printJournalToday(user, currentDate || new Date());
      } else {
        await printJournalRange(user, startDate, endDate);
      }
      setShowModal(false);
    } catch (err) {
      console.error('[OPP] Journal print error:', err);
      setError('Print failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* ── Print button ── */}
      <button
        onClick={handlePrint}
        disabled={loading}
        title={`Print ${currentView === 'notes' ? 'Journal' : currentView === 'tasks' ? 'My Day' : ''}`}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors px-2 py-1 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Printer size={14} />
        <span>Print</span>
      </button>

      {/* ── Journal print modal ── */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4">
            {/* Modal header */}
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-slate-800">Print Journal</h2>
              <button
                onClick={() => { setShowModal(false); setError(''); }}
                className="p-1 text-slate-400 hover:text-slate-600 rounded"
              >
                <X size={18} />
              </button>
            </div>

            {/* Mode selector */}
            <div className="flex gap-2 mb-5">
              <button
                onClick={() => { setPrintMode('today'); setError(''); }}
                className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-all ${
                  printMode === 'today'
                    ? 'text-white border-transparent'
                    : 'text-slate-600 border-slate-300 hover:bg-slate-50'
                }`}
                style={printMode === 'today' ? { backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' } : {}}
              >
                Today's Entry
              </button>
              <button
                onClick={() => { setPrintMode('range'); setError(''); }}
                className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-all ${
                  printMode === 'range'
                    ? 'text-white border-transparent'
                    : 'text-slate-600 border-slate-300 hover:bg-slate-50'
                }`}
                style={printMode === 'range' ? { backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' } : {}}
              >
                Date Range
              </button>
            </div>

            {/* Date range pickers */}
            {printMode === 'range' && (
              <div className="space-y-3 mb-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setError(''); }}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none"
                    style={{ '--tw-ring-color': 'var(--color-primary)' }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setError(''); }}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none"
                    min={startDate}
                  />
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <p className="text-xs text-red-600 mb-3">{error}</p>
            )}

            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => { setShowModal(false); setError(''); }}
                className="flex-1 py-2 text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleJournalPrint}
                disabled={loading}
                className="flex-1 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                <Printer size={14} />
                {loading ? 'Preparing...' : 'Print'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
